/* ==========================================================
   src/pages/Pedidos.jsx    (versión con clases CSS)
   ========================================================== */
   import React, { useEffect, useState } from 'react';
   import {
     collection,
     getDocs,
     getDoc,
     addDoc,
     updateDoc,
     doc
   } from 'firebase/firestore';
   import { db } from '../firebase';
   import { useUser } from '../context/UserContext';
   import jsPDF from 'jspdf';
   import autoTable from 'jspdf-autotable';
   import logo from '../assets/logo.png';
   
   const RIF_EMPRESA = 'J506844261';
   const DIR_EMPRESA =
     'Calle Ribas Dávila, Edif. Ribas PB Loc. 2‑3‑4, Urb. La Calera, La Victoria, Edo. Aragua';
   
   export default function Pedidos() {
     /* --------------------- contexto --------------------- */
     const { tasaBCV } = useUser();
   
     /* ----------------- catálogos base ------------------- */
     const [proveedores, setProveedores] = useState([]);
     const [productos,   setProductos]   = useState([]);
   
     /* --------------------- pedidos ---------------------- */
     const [pedidos, setPedidos] = useState([]);
     const pendientes  = pedidos.filter(p => p.estado === 'pendiente');
     const confirmados = pedidos.filter(p => p.estado === 'confirmado');
     const confPorProv = confirmados.reduce((acc, p) => {
       (acc[p.proveedorId] = acc[p.proveedorId] || []).push(p);
       return acc;
     }, {});
     const [showConfirmados, setShowConfirmados] = useState(false);
     const [provExpandido,   setProvExpandido]   = useState({});
   
     /* ------------- nuevo pedido (pasos) ----------------- */
     const [proveedorSel, setProveedorSel] = useState('');
     const [lineas,       setLineas]       = useState([]);
     const [paso,         setPaso]         = useState('crear');   // crear | revision
     const [busqueda,     setBusqueda]     = useState('');
   
     /* modal añadir producto */
     const [modalProd,  setModalProd] = useState(false);
     const [tmpProd,    setTmpProd]   = useState(null);
     const [tmpCant,    setTmpCant]   = useState(1);
     const [tmpNombre,  setTmpNombre] = useState('');
   
     /* modal recepción */
     const [modalRecep,   setModalRecep]   = useState(null);
     const [fechaTopeRec, setFechaTopeRec] = useState('');
   
     /* detalle drawer */
     const [pedidoActivo, setPedidoActivo] = useState(null);
     const [editando,     setEditando]     = useState(false);
   
     /* proveedor inline */
     const [verFormProv, setVerFormProv] = useState(false);
     const [provNom,  setProvNom]  = useState('');
     const [provRif,  setProvRif]  = useState('');
     const [provTipo, setProvTipo] = useState('natural');
     const [provRet,  setProvRet]  = useState(75);
   
     /* ------------------- carga inicial ------------------ */
     useEffect(() => {
       (async () => {
         const snapProv = await getDocs(collection(db, 'proveedores'));
         setProveedores(snapProv.docs.map(d => ({ id: d.id, ...d.data() })));
   
         const snapProd = await getDocs(collection(db, 'inventario'));
         setProductos(snapProd.docs.map(d => ({ id: d.id, ...d.data() })));
   
         const snapPed  = await getDocs(collection(db, 'pedidos'));
         setPedidos(snapPed.docs.map(d => ({ id: d.id, ...d.data() })));
       })();
     }, []);
   
     /* ------------------- helpers ------------------------ */
     const setLinea = (idx, key, val) =>
       setLineas(prev => {
         const arr = [...prev];
         arr[idx] = { ...arr[idx], [key]: val };
         return arr;
       });
   
     const totalUSD = arr => {
       const rate = parseFloat(tasaBCV);
       return arr.reduce((acc, l) =>
         acc + (l.monedaCompra === 'USD'
           ? l.precioCompra * l.cantRec
           : (rate ? (l.precioCompra * l.cantRec) / rate : 0)), 0);
     };
   
     /* ---------------- modal producto ------------------- */
     const abrirModalProd = (prod, manual = false) => {
       setTmpProd({ ...prod, manual });
       setTmpCant(1); setTmpNombre(''); setModalProd(true);
     };
   
     const confirmarModalProd = () => {
       if (tmpProd.id === null && !tmpNombre.trim()) return alert('Nombre requerido');
       const qty = parseInt(tmpCant, 10); if (!qty) return;
       setLineas(prev => [
         ...prev,
         {
           id           : tmpProd.id,
           codigo       : tmpProd.codigo || '',
           nombre       : tmpProd.id ? tmpProd.nombre : tmpNombre.trim(),
           cantPedida   : qty,
           cantRec      : qty,
           precioCompra : 0,
           monedaCompra : 'USD',
           precioVenta  : tmpProd.precio || 0,
           nuevo        : !tmpProd.id
         }
       ]);
       setModalProd(false);
     };
   
     /* -------------- guardar proveedor inline ------------ */
     const guardarProv = async () => {
       if (!provNom.trim()) return alert('Nombre requerido');
       const ref = await addDoc(collection(db, 'proveedores'), {
         nombre: provNom, rif: provRif,
         tipo: provTipo, porcentajeRetencionIVA: provTipo === 'jurídico' ? provRet : 0
       });
       const snap = await getDoc(ref);
       setProveedores(p => [...p, { id: ref.id, ...snap.data() }]);
       setProveedorSel(ref.id);
       setVerFormProv(false);
       setProvNom(''); setProvRif('');
     };
   
     /* ---------------- guardar pedido ------------------- */
     const guardarPedido = async () => {
       if (!proveedorSel || lineas.length === 0) return alert('Faltan datos');
       const ref = await addDoc(collection(db, 'pedidos'), {
         proveedorId: proveedorSel,
         lineas,
         fecha: new Date().toISOString(),
         estado: 'pendiente'
       });
       setPedidos(p => [...p, {
         id: ref.id, proveedorId: proveedorSel, lineas,
         fecha: new Date().toISOString(), estado: 'pendiente'
       }]);
       setProveedorSel(''); setLineas([]); setBusqueda(''); setPaso('crear');
     };
   
     /* ------------------- PDF pedido -------------------- */
     const imprimirPedido = ped => {
       const pdf = new jsPDF();
       pdf.setFontSize(16);
       pdf.text('Pedido a proveedor', 14, 16);
       const prov = proveedores.find(p => p.id === ped.proveedorId);
       pdf.setFontSize(12);
       pdf.text(`Proveedor: ${prov?.nombre || ped.proveedorId}`, 14, 24);
   
       if (ped.estado === 'pendiente') {
         autoTable(pdf, {
           head: [['Producto', 'Cantidad']],
           body: ped.lineas.map(l => [l.nombre, l.cantPedida]),
           startY: 32
         });
       } else {
         autoTable(pdf, {
           head: [['Producto', 'Cant.', 'Moneda', 'Costo']],
           body: ped.lineas.map(l => [l.nombre, l.cantPedida, l.monedaCompra, l.precioCompra.toFixed(2)]),
           startY: 32
         });
       }
   
       const h = pdf.internal.pageSize.getHeight();
       pdf.setFontSize(10);
       pdf.text(`RIF: ${RIF_EMPRESA}`, 14, h - 20);
       pdf.text(DIR_EMPRESA, 14, h - 14, { maxWidth: 180 });
       pdf.addImage(logo, 'PNG', 150, h - 28, 38, 0);
       pdf.save(`pedido_${ped.id}.pdf`);
     };
   
     /* -------------- confirmar recepción ---------------- */
     const confirmarRecepcion = async (ped, fechaTop) => {
       const totalUsd = totalUSD(ped.lineas);
   
       for (const l of ped.lineas) {
         const qty = l.cantRec;
         if (l.nuevo) {
           const cod = l.codigo || prompt(`Código para ${l.nombre}:`, '');
           await addDoc(collection(db, 'inventario'), {
             nombre: l.nombre, codigo: cod, cantidad: qty,
             precio: l.precioVenta, fechaServicio: new Date().toISOString()
           });
         } else {
           const ref = doc(db, 'inventario', l.id);
           const snap = await getDoc(ref);
           await updateDoc(ref, {
             cantidad: (snap.data().cantidad || 0) + qty,
             precio: l.precioVenta
           });
         }
       }
   
       await updateDoc(doc(db, 'pedidos', ped.id), {
         estado: 'confirmado',
         fechaConfirmacion: new Date().toISOString()
       });
       setPedidos(arr => arr.map(p => p.id === ped.id
         ? { ...p, estado: 'confirmado', fechaConfirmacion: new Date().toISOString() } : p));
   
       await addDoc(collection(db, 'facturas'), {
         proveedorId: ped.proveedorId,
         monto: totalUsd,
         montoBS: parseFloat(tasaBCV) ? totalUsd * parseFloat(tasaBCV) : 0,
         estado: 'pendiente',
         fecha: new Date().toISOString(),
         fechaTope: fechaTop,
         descripcion: `Pedido ${ped.id}`
       });
       setModalRecep(null); setPedidoActivo(null);
       alert('Inventario actualizado y factura creada');
     };
   
     /* ------------------- búsqueda ---------------------- */
     const resultados = busqueda
       ? productos.filter(p =>
           p.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
           p.codigo?.toLowerCase().includes(busqueda.toLowerCase()))
       : [];
   
     /* ======================= RENDER ===================== */
     return (
       <div className="pedidos-container">
         <h2>Pedidos a Proveedores</h2>
   
         {/* ---------------- pendientes ---------------- */}
         <h3>Pendientes</h3>
         <div className="pedidos-grid">
           {pendientes.map(p => (
             <div
               key={p.id}
               className="pedido-card"
             >
               <strong>{p.id.slice(0, 6)}…</strong><br />
               <span className="estado-pend">pendiente</span><br />
               <em>{proveedores.find(pr => pr.id === p.proveedorId)?.nombre}</em><br />
               <button onClick={() => { setPedidoActivo(p); setEditando(false); }}>
                 Ver
               </button>
             </div>
           ))}
         </div>
   
         {/* ------------- botón colapsable confirmados ------------- */}
         <button
           className="toggle-btn"
           onClick={() => setShowConfirmados(!showConfirmados)}
         >
           {showConfirmados ? 'Ocultar pedidos confirmados' : 'Ver pedidos confirmados'}
         </button>
   
         {/* ------------- sección confirmados ------------- */}
         {showConfirmados && (
           <div style={{ marginBottom: 28 }}>
             {Object.entries(confPorProv).map(([provId, lista]) => {
               const prov = proveedores.find(p => p.id === provId);
               const abierto = !!provExpandido[provId];
               return (
                 <div key={provId} style={{ marginBottom: 12 }}>
                   <div
                     className="folder"
                     onClick={() => setProvExpandido(p => ({ ...p, [provId]: !abierto }))}
                   >
                     <span>{prov?.nombre || provId} ({lista.length})</span>
                     <span>{abierto ? '▲' : '▼'}</span>
                   </div>
                   {abierto && (
                     <div className="folder-items">
                       {lista.map(p => (
                         <div
                           key={p.id}
                           className="pedido-card confirmado"
                         >
                           <strong>{p.id.slice(0, 6)}…</strong><br />
                           <small>{new Date(p.fechaConfirmacion).toLocaleDateString()}</small><br />
                           <button onClick={() => setPedidoActivo(p)}>Ver</button>
                         </div>
                       ))}
                     </div>
                   )}
                 </div>
               );
             })}
           </div>
         )}
   
         <hr style={{ borderColor: '#333' }} />
   
         {/* ---------------- nuevo pedido ---------------- */}
         <h3 className="nuevo-header">Nuevo pedido</h3>
   
         {/* selector proveedor */}
         <div className="nuevo-prov-select">
           <label>Proveedor:&nbsp;</label>
           <select value={proveedorSel} onChange={e => setProveedorSel(e.target.value)}>
             <option value="">-- Selecciona --</option>
             {proveedores.map(pr => (
               <option key={pr.id} value={pr.id}>{pr.nombre}</option>
             ))}
           </select>
           <button onClick={() => setVerFormProv(!verFormProv)}>
             {verFormProv ? 'Cancelar' : 'Nuevo proveedor'}
           </button>
         </div>
   
         {/* form proveedor nuevo */}
         {verFormProv && (
           <div className="form-prov">
             <h4 style={{ margin: 0 }}>Agregar proveedor</h4>
             <input
               placeholder="Nombre"
               value={provNom}
               onChange={e => setProvNom(e.target.value)}
             />
             <input
               placeholder="RIF"
               value={provRif}
               onChange={e => setProvRif(e.target.value)}
             />
             <select value={provTipo} onChange={e => setProvTipo(e.target.value)}>
               <option value="natural">Natural</option>
               <option value="jurídico">Jurídico</option>
             </select>
             {provTipo === 'jurídico' && (
               <select value={provRet} onChange={e => setProvRet(parseInt(e.target.value, 10))}>
                 <option value={75}>Retiene 75%</option>
                 <option value={100}>Retiene 100%</option>
               </select>
             )}
             <button onClick={guardarProv}>Guardar</button>
           </div>
         )}
   
         {/* paso CREAR */}
         {paso === 'crear' && (
           <>
             <input
               className="buscar-input"
               type="text"
               placeholder="Buscar producto…"
               value={busqueda}
               onChange={e => setBusqueda(e.target.value)}
             />
             {busqueda && resultados.length === 0 && (
               <button className="new-prod-btn"
                 onClick={() => abrirModalProd({ id: null, codigo: '', nombre: '', precio: 0 }, true)}
               >
                 Nuevo producto
               </button>
             )}
   
             <div className="resultados-busqueda">
               {resultados.map(r => (
                 <div key={r.id} className="card-resultado">
                   {r.nombre}<br />
                   <button onClick={() => abrirModalProd(r)}>Añadir</button>
                 </div>
               ))}
             </div>
   
             {lineas.length > 0 && (
               <>
                 <table className="res-table">
                   <thead>
                     <tr>
                       <th style={{ textAlign: 'left' }}>Producto</th>
                       <th>Cant.</th>
                       <th colSpan={2}>Ajuste</th>
                       <th>Nuevo?</th>
                     </tr>
                   </thead>
                   <tbody>
                     {lineas.map((l, i) => (
                       <tr key={i}>
                         <td style={{ textAlign: 'left' }}>{l.nombre}</td>
                         <td>{l.cantPedida}</td>
                         <td>
                           <button onClick={() => setLinea(i, 'cantPedida', l.cantPedida + 1)}>+</button>
                         </td>
                         <td>
                           <button onClick={() => setLinea(i, 'cantPedida', Math.max(1, l.cantPedida - 1))}>-</button>
                         </td>
                         <td>{l.nuevo ? 'Sí' : 'No'}</td>
                       </tr>
                     ))}
                   </tbody>
                 </table>
   
                 <button className="btn-volver" onClick={() => setPaso('revision')}>
                   Revisar pedido
                 </button>
                 <button onClick={guardarPedido}>Guardar pendiente</button>
               </>
             )}
           </>
         )}
   
         {/* paso REVISION */}
         {paso === 'revision' && (
           <>
             <h4>Revisión del pedido</h4>
   
             <table className="res-table">
               <thead>
                 <tr>
                   <th>Prod.</th><th>Código</th><th>Pedida</th><th>Recib.</th>
                   <th>Mon.</th><th>Compra</th><th>Venta</th>
                 </tr>
               </thead>
               <tbody>
                 {lineas.map((l, i) => (
                   <tr key={i}>
                     <td style={{ textAlign: 'left' }}>{l.nombre}</td>
                     <td>
                       <input
                         value={l.codigo}
                         onChange={e => setLinea(i, 'codigo', e.target.value)}
                       />
                     </td>
                     <td>{l.cantPedida}</td>
                     <td>
                       <input
                         type="number"
                         value={l.cantRec}
                         onChange={e => setLinea(i, 'cantRec', parseInt(e.target.value, 10) || 0)}
                       />
                     </td>
                     <td>
                       <select
                         value={l.monedaCompra}
                         onChange={e => setLinea(i, 'monedaCompra', e.target.value)}
                       >
                         <option value="USD">USD</option>
                         <option value="BS">Bs</option>
                       </select>
                     </td>
                     <td>
                       <input
                         type="number"
                         step="0.01"
                         value={l.precioCompra}
                         onChange={e => setLinea(i, 'precioCompra', parseFloat(e.target.value) || 0)}
                       />
                     </td>
                     <td>
                       <input
                         type="number"
                         step="0.01"
                         value={l.precioVenta}
                         onChange={e => setLinea(i, 'precioVenta', parseFloat(e.target.value) || 0)}
                       />
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
   
             <div className="pedidos-total-box">
               Subtotal USD ≈ ${totalUSD(lineas).toFixed(2)}
             </div>
   
             <button className="btn-volver" onClick={() => setPaso('crear')}>
               Volver
             </button>
             <button onClick={guardarPedido}>Guardar pendiente</button>
           </>
         )}
   
         {/* -------------- modal producto -------------- */}
         {modalProd && (
           <div className="modal-mask">
             <div className="modal-box">
               <h4 style={{ marginTop: 0 }}>
                 {tmpProd.id ? 'Cantidad a pedir' : 'Nuevo producto'}
               </h4>
   
               {!tmpProd.id && (
                 <input
                   placeholder="Nombre"
                   value={tmpNombre}
                   onChange={e => setTmpNombre(e.target.value)}
                   style={{ width: '100%', marginBottom: 10 }}
                 />
               )}
   
               {tmpProd.id && <p>{tmpProd.nombre}</p>}
   
               <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                 <button onClick={() => setTmpCant(Math.max(1, tmpCant - 1))}>-</button>
                 <input
                   type="number"
                   min="1"
                   value={tmpCant}
                   onChange={e => setTmpCant(parseInt(e.target.value, 10) || 1)}
                   style={{ width: 70, textAlign: 'center' }}
                 />
                 <button onClick={() => setTmpCant(tmpCant + 1)}>+</button>
               </div>
   
               <div style={{ textAlign: 'right', marginTop: 18 }}>
                 <button onClick={() => setModalProd(false)}>Cancelar</button>
                 <button onClick={confirmarModalProd} style={{ marginLeft: 10 }}>
                   Confirmar
                 </button>
               </div>
             </div>
           </div>
         )}
   
         {/* -------------- modal recepción -------------- */}
         {modalRecep && (
           <div className="modal-mask">
             <div className="modal-box" style={{ width: '90%', maxWidth: 420 }}>
               <h4 style={{ marginTop: 0 }}>Confirmar recepción</h4>
               <p>Total USD ≈ <strong>${totalUSD(modalRecep.lineas).toFixed(2)}</strong></p>
   
               <label>Fecha tope de pago:</label><br />
               <input
                 type="date"
                 value={fechaTopeRec}
                 onChange={e => setFechaTopeRec(e.target.value)}
                 style={{ marginTop: 6, marginBottom: 14 }}
               />
   
               <div style={{ textAlign: 'right' }}>
                 <button onClick={() => setModalRecep(null)}>Cancelar</button>
                 <button
                   onClick={() => {
                     if (!fechaTopeRec) return alert('Selecciona fecha tope');
                     confirmarRecepcion(modalRecep, fechaTopeRec);
                   }}
                   style={{ marginLeft: 10 }}
                 >
                   Confirmar
                 </button>
               </div>
             </div>
           </div>
         )}
   
         {/* -------------- drawer detalle -------------- */}
         {pedidoActivo && (
           <div className="drawer">
             <h3>Pedido {pedidoActivo.id}</h3>
             <p>
               <strong>Proveedor:</strong>{' '}
               {proveedores.find(p => p.id === pedidoActivo.proveedorId)?.nombre ||
                 pedidoActivo.proveedorId}
             </p>
             <p><strong>Estado:</strong> {pedidoActivo.estado}</p>
             {pedidoActivo.fechaConfirmacion && (
               <p>
                 <strong>Confirmado:</strong>{' '}
                 {new Date(pedidoActivo.fechaConfirmacion).toLocaleDateString()}
               </p>
             )}
   
             <table>
               <thead>
                 <tr>
                   <th>Prod.</th><th>Cód</th><th>Ped</th><th>Rec</th>
                   <th>Mon</th><th>Compra</th><th>Venta</th>
                 </tr>
               </thead>
               <tbody>
                 {pedidoActivo.lineas.map((l, i) => (
                   <tr key={i}>
                     <td style={{ textAlign: 'left' }}>{l.nombre}</td>
                     <td>
                       {editando ? (
                         <input
                           value={l.codigo}
                           onChange={e => {
                             const arr = [...pedidoActivo.lineas];
                             arr[i].codigo = e.target.value;
                             setPedidoActivo({ ...pedidoActivo, lineas: arr });
                           }}
                         />
                       ) : l.codigo || '-'}
                     </td>
                     <td>{l.cantPedida}</td>
                     <td>
                       {editando ? (
                         <input
                           type="number"
                           value={l.cantRec}
                           onChange={e => {
                             const arr = [...pedidoActivo.lineas];
                             arr[i].cantRec = parseInt(e.target.value, 10) || 0;
                             setPedidoActivo({ ...pedidoActivo, lineas: arr });
                           }}
                         />
                       ) : l.cantRec}
                     </td>
                     <td>
                       {editando ? (
                         <select
                           value={l.monedaCompra}
                           onChange={e => {
                             const arr = [...pedidoActivo.lineas];
                             arr[i].monedaCompra = e.target.value;
                             setPedidoActivo({ ...pedidoActivo, lineas: arr });
                           }}
                         >
                           <option value="USD">USD</option>
                           <option value="BS">Bs</option>
                         </select>
                       ) : l.monedaCompra}
                     </td>
                     <td>
                       {editando ? (
                         <input
                           type="number"
                           step="0.01"
                           value={l.precioCompra}
                           onChange={e => {
                             const arr = [...pedidoActivo.lineas];
                             arr[i].precioCompra = parseFloat(e.target.value) || 0;
                             setPedidoActivo({ ...pedidoActivo, lineas: arr });
                           }}
                         />
                       ) : l.precioCompra.toFixed(2)}
                     </td>
                     <td>
                       {editando ? (
                         <input
                           type="number"
                           step="0.01"
                           value={l.precioVenta}
                           onChange={e => {
                             const arr = [...pedidoActivo.lineas];
                             arr[i].precioVenta = parseFloat(e.target.value) || 0;
                             setPedidoActivo({ ...pedidoActivo, lineas: arr });
                           }}
                         />
                       ) : l.precioVenta.toFixed(2)}
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
   
             <div className="pedidos-total-box">
               Total USD ≈ ${totalUSD(pedidoActivo.lineas).toFixed(2)}
             </div>
   
             <button onClick={() => imprimirPedido(pedidoActivo)}>
               Imprimir PDF
             </button>
   
             {pedidoActivo.estado === 'pendiente' && (
               <>
                 {!editando ? (
                   <button
                     onClick={() => setEditando(true)}
                     style={{ marginLeft: 10 }}
                   >
                     Editar / Confirmar recepción
                   </button>
                 ) : (
                   <>
                     <button
                       onClick={async () => {
                         await updateDoc(doc(db, 'pedidos', pedidoActivo.id), {
                           lineas: pedidoActivo.lineas
                         });
                         setPedidos(arr => arr.map(p =>
                           p.id === pedidoActivo.id ? { ...p, lineas: pedidoActivo.lineas } : p
                         ));
                         setEditando(false);
                         alert('Cambios guardados');
                       }}
                       style={{ marginLeft: 10 }}
                     >
                       Guardar cambios
                     </button>
                     <button
                       onClick={() => setModalRecep(pedidoActivo)}
                       style={{ marginLeft: 10 }}
                     >
                       Confirmar recepción
                     </button>
                   </>
                 )}
               </>
             )}
   
             <button onClick={() => setPedidoActivo(null)} style={{ marginLeft: 10 }}>
               Cerrar
             </button>
           </div>
         )}
       </div>
     );
   }
   